/**
 * FeedbackTab (SK-FEEDBACK, T-23, DS-1g-05 §5.1 / spec §8c) — AC-1g-29,
 * tasks #7 (reach a human) and #8 (send debug logs).
 *
 * WHAT CHANGED AND WHY (2026-07-27). This screen used to post feedback through
 * the normal §1 capture outbox as an Event with `skill: "feedback"`. That was
 * broken twice over, and both halves were invisible from the UI:
 *
 *   1. `submitFeedback` called `saveToProject` and nothing released the record,
 *      so it sat HELD in IndexedDB forever and was never dispatched at all.
 *   2. Had it dispatched, the coordinator would have refused it: `feedback` is
 *      not in the ROUTING_TABLE, so `CaptureLogged` routes to
 *      `daily_field_log_capture` and the route-conflict check returns a
 *      permanent 422 (verified live against the running coordinator).
 *
 * Meanwhile the screen said "Thanks — sent." on local commit. Every field report
 * anyone has ever typed into this app is still on their phone.
 *
 * So feedback now takes its own server path (`POST /mark/feedback` → durable
 * sink → best-effort mail to dev@buildengine.ai). A field report belongs to
 * BuildEngine, not to the tenant's project record; it should never have shared
 * rails with a jobsite observation in the first place.
 *
 * The confirmation is now only as true as the server's answer — no local queue,
 * no optimistic ack. If the send fails the typed text stays exactly where it is
 * and the screen says so, which is honest and needs no second outbox to
 * maintain. Re-introducing an optimistic ack here would recreate the exact bug
 * this replaces.
 *
 * AND IT NEARLY DID (2026-07-28). The same false claim came back one boundary
 * later: `/mark/feedback` returns 200 for STORED, with mail best-effort and
 * reported separately as `emailed` — but this screen said "Thanks — sent." on
 * `ok` alone. With no `MARK_SMTP_HOST` configured, two real reports were stored
 * correctly and nothing was ever mailed, while the screen claimed otherwise.
 * The transport had carried `emailed` faithfully the whole time; the loss was
 * here. The rule generalises: a confirmation may only assert the step the server
 * actually confirmed, and "the request succeeded" is not the same step as "a
 * human will receive this."
 *
 * §4 (calm, no surprises): "Send debug logs" is a SEPARATE, affirmative action.
 * Device state never rides along silently with a written report — a super should
 * know when their phone's state leaves the phone.
 */

import { useState } from "preact/hooks";
import { buildDiagnostics } from "../feedback/diagnostics";
import type { FeedbackClient, SendOutcome } from "../feedback/feedback-client";
import type { OutboxRecord } from "../outbox/types";
import { Chip } from "./Chip";

export const FEEDBACK_LABEL = "feedback";

export interface FeedbackTabProps {
  tenant_id: string;
  /** AC-1g-07 (TW-1g-PROJECT-INFER): the user-selected, RBAC-scoped
   * project_ref (or `null` until the human picks one). A report is scoped to a
   * project like every other emission — submission is BLOCKED with no
   * selection; never inferred/defaulted. */
  project_ref: string | null;
  /** The screen the human was on when they tapped Feedback (spec §8c's
   * "optional current-screen context") — free-form, never a triage/
   * judgment field. */
  screenContext?: string;
  /** The transport. `null` when no endpoint is baked in (dev/test builds) — the
   * screen then refuses to claim anything was sent, rather than showing a
   * confirmation for a report that went nowhere. */
  client: FeedbackClient | null;
  /** Live outbox snapshot for the #8 bundle — summarized to counts/states by
   * `buildDiagnostics`; no payload text and no blob bytes ever leave here. */
  records?: OutboxRecord[];
  /** Whether a service-worker update is waiting — the single most useful bit
   * for the stale-bundle class of bug. */
  updateReady?: boolean;
  onSent?: (kind: "feedback" | "debug") => void;
}

type Status =
  | { phase: "idle" }
  | { phase: "sending" }
  /** `emailed` is the server's OWN answer about the notification half. The 200
   * from `/mark/feedback` means STORED; mail is best-effort and reported
   * separately, so claiming "sent" on a 200 alone overstates what happened. */
  | { phase: "sent"; kind: "feedback" | "debug"; emailed: boolean }
  | { phase: "failed"; retryable: boolean; error: string };

export function FeedbackTab({
  tenant_id,
  project_ref,
  screenContext,
  client,
  records = [],
  updateReady = false,
  onSent,
}: FeedbackTabProps) {
  const [textDraft, setTextDraft] = useState("");
  const [status, setStatus] = useState<Status>({ phase: "idle" });

  function applyOutcome(outcome: SendOutcome, kind: "feedback" | "debug") {
    if (outcome.ok) {
      setStatus({ phase: "sent", kind, emailed: outcome.emailed });
      onSent?.(kind);
    } else {
      setStatus({ phase: "failed", retryable: outcome.retryable, error: outcome.error });
    }
  }

  async function handleSubmit() {
    const trimmed = textDraft.trim();
    if (!trimmed) return; // human-typed only — no default/placeholder text sent
    if (project_ref === null) return; // AC-1g-07: no project, no emit
    if (!client) {
      setStatus({ phase: "failed", retryable: false, error: "no_endpoint" });
      return;
    }
    setStatus({ phase: "sending" });
    const outcome = await client.send({
      kind: "feedback",
      project_ref,
      message: screenContext ? `${trimmed}\n\n[screen: ${screenContext}]` : trimmed,
    });
    // The draft is cleared ONLY on a confirmed send. Clearing it on failure
    // would destroy the human's words in the one case they still need them.
    if (outcome.ok) setTextDraft("");
    applyOutcome(outcome, "feedback");
  }

  async function handleSendDebug() {
    if (project_ref === null) return;
    if (!client) {
      setStatus({ phase: "failed", retryable: false, error: "no_endpoint" });
      return;
    }
    setStatus({ phase: "sending" });
    const diagnostics = await buildDiagnostics({
      records,
      tenant_id,
      project_ref,
      updateReady,
    });
    const trimmed = textDraft.trim();
    const outcome = await client.send({
      kind: "debug",
      project_ref,
      // Whatever they had typed rides along — if someone wrote "it froze here"
      // and then tapped Send debug logs, throwing that away would be perverse.
      ...(trimmed ? { message: trimmed } : {}),
      diagnostics,
    });
    if (outcome.ok) setTextDraft("");
    applyOutcome(outcome, "debug");
  }

  const busy = status.phase === "sending";
  const blocked = project_ref === null || !client;

  return (
    <section data-skin-slot="SK-FEEDBACK" data-testid="feedback-tab">
      <div style="font-size:20px;font-weight:700;color:var(--mark-text-primary)">Feedback</div>
      <label>
        What's going on?
        <textarea
          data-testid="feedback-tab-text"
          placeholder="Describe the bug or idea…"
          value={textDraft}
          onInput={(e) => {
            setTextDraft((e.target as HTMLTextAreaElement).value);
            setStatus({ phase: "idle" });
          }}
        />
      </label>
      {project_ref === null && (
        <p data-testid="feedback-tab-no-project-reason">
          Pick a project before sending feedback — every report is scoped to a project.
        </p>
      )}
      <Chip
        data-testid="feedback-tab-submit"
        disabled={textDraft.trim().length === 0 || blocked || busy}
        onClick={handleSubmit}
      >
        Send
      </Chip>

      {/* #8: a separate, affirmative action — never silent telemetry attached
          to a written report. One extra control on an intentionally bare
          screen (rule #1). */}
      <Chip
        data-testid="feedback-tab-send-debug"
        disabled={blocked || busy}
        onClick={handleSendDebug}
        style="margin-top:12px"
      >
        Send debug logs
      </Chip>
      <p data-testid="feedback-tab-debug-hint" style="font-size:11.5px;color:var(--mark-text-muted)">
        Sends this phone's app version, queue status and recent errors — no photos, no audio, no
        recordings of what you said.
      </p>

      {/* Two different true statements, never one convenient one. "Sent" is
          claimed ONLY when the server says it actually mailed the report;
          otherwise the report is safely stored and waiting, and the screen says
          that instead. Both outcomes are a success for the human — the report is
          not lost either way — so neither is styled as an error. */}
      {status.phase === "sent" && (
        <p data-testid="feedback-tab-confirmation" data-emailed={String(status.emailed)}>
          {status.emailed
            ? status.kind === "debug"
              ? "Thanks — debug logs sent."
              : "Thanks — sent."
            : status.kind === "debug"
              ? "Thanks — debug logs stored safely. Email notification was unavailable."
              : "Thanks — your feedback is stored safely. Email notification was unavailable."}
        </p>
      )}
      {status.phase === "failed" && (
        <p data-testid="feedback-tab-failure">
          {status.retryable
            ? "Couldn't send — no connection. Your note is still here; try again when you have signal."
            : `Couldn't send — the server wouldn't accept this report (${status.error}).`}
        </p>
      )}
    </section>
  );
}
